import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  CircularProgress
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import AddIcon from "@mui/icons-material/Add";
import CampaignIcon from "@mui/icons-material/Campaign";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import ForbiddenPage from "../../components/ForbiddenPage";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";

// ---------------------------------------------------------------------------
// Global Styles (mesmo do Dashboard)
// ---------------------------------------------------------------------------
const globalStyles = `
  @keyframes pulse-ring {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 1; }
  }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = globalStyles;
  document.head.appendChild(styleSheet);
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_CAMPAIGNS": {
      const campaigns = action.payload;
      const newCampaigns = [];
      if (isArray(campaigns)) {
        campaigns.forEach((campaign) => {
          const idx = state.findIndex((u) => u.id === campaign.id);
          if (idx !== -1) {
            state[idx] = campaign;
          } else {
            newCampaigns.push(campaign);
          }
        });
      }
      return [...state, ...newCampaigns];
    }
    case "UPDATE_CAMPAIGNS": {
      const campaign = action.payload;
      const idx = state.findIndex((u) => u.id === campaign.id);
      if (idx !== -1) {
        state[idx] = campaign;
        return [...state];
      }
      return [campaign, ...state];
    }
    case "DELETE_CAMPAIGN": {
      const campaignId = action.payload;
      const idx = state.findIndex((u) => u.id === campaignId);
      if (idx !== -1) state.splice(idx, 1);
      return [...state];
    }
    case "RESET":
      return [];
    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------
const Campaigns = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  // --- State ---
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, []);

  // ---------------------------------------------------------------------------
  // Permissão do plano
  // ---------------------------------------------------------------------------
  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, user.companyId);
      if (!planConfigs.plan.useCampaigns) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => history.push("/"), 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delay = setTimeout(() => { fetchCampaigns(); }, 500);
    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns/", {
        params: { searchParam, pageNumber }
      });
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  // ---------------------------------------------------------------------------
  // Socket
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const companyId = user.companyId;
    const onCompanyCampaign = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    };
    socket.on(`company-${companyId}-campaign`, onCompanyCampaign);
    return () => socket.off(`company-${companyId}-campaign`, onCompanyCampaign);
  }, [user, socket]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":       return "Inativa";
      case "PROGRAMADA":    return "Programada";
      case "EM_ANDAMENTO":  return "Em Andamento";
      case "CANCELADA":     return "Cancelada";
      case "FINALIZADA":    return "Finalizada";
      default:              return val;
    }
  };

  const getStatusChip = (status) => {
    const configs = {
      INATIVA:       { bg: "rgba(148, 163, 184, 0.08)", color: "text.secondary", border: "rgba(148, 163, 184, 0.2)" },
      PROGRAMADA:    { bg: "rgba(59, 130, 246, 0.08)",  color: "#3b82f6", border: "rgba(59, 130, 246, 0.2)" },
      EM_ANDAMENTO:  { bg: "rgba(16, 185, 129, 0.08)",  color: "#10b981", border: "rgba(16, 185, 129, 0.2)" },
      CANCELADA:     { bg: "rgba(239, 68, 68, 0.08)",   color: "#ef4444", border: "rgba(239, 68, 68, 0.2)" },
      FINALIZADA:    { bg: "rgba(139, 92, 246, 0.08)",  color: "#8b5cf6", border: "rgba(139, 92, 246, 0.2)" }
    };
    const cfg = configs[status] || configs.INATIVA;
    return (
      <Chip
        label={formatStatus(status)}
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: "0.75rem",
          bgcolor: cfg.bg,
          color: cfg.color,
          border: "1px solid",
          borderColor: cfg.border
        }}
      />
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (user.profile === "user") {
    return <ForbiddenPage />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <Container maxWidth="xl">

        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <CampaignIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                {i18n.t("campaigns.title")}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                Gerenciamento de campanhas
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2}>
            {/* Search */}
            <TextField
              placeholder={i18n.t("campaigns.searchPlaceholder")}
              type="search"
              size="small"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.disabled", fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
              sx={{
                width: { xs: 200, sm: 280 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#fff",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  "& fieldset": { border: "none" },
                  "&:hover": { 
                    borderColor: PRIMARY_MAIN,
                    bgcolor: "action.hover"
                  },
                  "&.Mui-focused": { 
                    borderColor: PRIMARY_MAIN,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)"
                  }
                },
                "& .MuiOutlinedInput-input": {
                  py: 1.25,
                  fontSize: "0.875rem",
                  fontWeight: 500
                }
              }}
            />

            {/* Add button */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCampaignModal}
              sx={{
                bgcolor: PRIMARY_MAIN,
                color: "#fff",
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                "&:hover": {
                  bgcolor: PRIMARY_DARK,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
                }
              }}
            >
              {i18n.t("campaigns.buttons.add")}
            </Button>
          </Stack>
        </Stack>

        {/* Tabela */}
        <Paper
          elevation={0}
          onScroll={handleScroll}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          {/* Cabeçalho da tabela */}
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                {searchParam ? `Resultados da busca` : "Todas as campanhas"}
              </Typography>
              <Chip
                label={`${campaigns.length} ${campaigns.length !== 1 ? "campanhas" : "campanha"}`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  bgcolor: "rgba(148, 163, 184, 0.08)",
                  color: "text.secondary",
                  border: "1px solid",
                  borderColor: "rgba(148, 163, 184, 0.2)"
                }}
              />
            </Stack>
          </Box>

          {/* Grid */}
          <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    i18n.t("campaigns.table.name"),
                    i18n.t("campaigns.table.status"),
                    i18n.t("campaigns.table.contactList"),
                    i18n.t("campaigns.table.whatsapp"),
                    i18n.t("campaigns.table.scheduledAt"),
                    i18n.t("campaigns.table.completedAt"),
                    i18n.t("campaigns.table.confirmation"),
                    i18n.t("campaigns.table.actions")
                  ].map((label) => (
                    <TableCell
                      key={label}
                      align="center"
                      sx={{
                        fontWeight: 600,
                        textTransform: "uppercase",
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        letterSpacing: "0.5px",
                        bgcolor: "background.paper"
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {campaigns.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhuma campanha encontrada
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  campaigns.map((campaign) => (
                    <TableRow
                      key={campaign.id}
                      sx={{
                        "&:hover": {
                          bgcolor: "action.hover",
                          transition: "background-color 0.2s"
                        }
                      }}
                    >
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                          {campaign.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {getStatusChip(campaign.status)}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                          {campaign.contactListId ? campaign.contactList.name : "Não definida"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                          {campaign.whatsappId ? campaign.whatsapp.name : "Não definido"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem", whiteSpace: "nowrap" }}>
                          {campaign.scheduledAt ? datetimeToClient(campaign.scheduledAt) : "Sem agendamento"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.875rem", whiteSpace: "nowrap" }}>
                          {campaign.completedAt ? datetimeToClient(campaign.completedAt) : "Não concluída"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={campaign.confirmation ? "Habilitada" : "Desabilitada"}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            bgcolor: campaign.confirmation ? "rgba(16, 185, 129, 0.08)" : "rgba(148, 163, 184, 0.08)",
                            color: campaign.confirmation ? "#10b981" : "text.secondary",
                            border: "1px solid",
                            borderColor: campaign.confirmation ? "rgba(16, 185, 129, 0.2)" : "rgba(148, 163, 184, 0.2)"
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {campaign.status === "EM_ANDAMENTO" && (
                            <Tooltip title="Parar Campanha">
                              <IconButton
                                size="small"
                                onClick={() => cancelCampaign(campaign)}
                                sx={{
                                  color: "text.secondary",
                                  "&:hover": { color: "#f59e0b", bgcolor: "rgba(245, 158, 11, 0.08)" }
                                }}
                              >
                                <PauseCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {campaign.status === "CANCELADA" && (
                            <Tooltip title="Reiniciar Campanha">
                              <IconButton
                                size="small"
                                onClick={() => restartCampaign(campaign)}
                                sx={{
                                  color: "text.secondary",
                                  "&:hover": { color: "#10b981", bgcolor: "rgba(16, 185, 129, 0.08)" }
                                }}
                              >
                                <PlayCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Relatório">
                            <IconButton
                              size="small"
                              onClick={() => history.push(`/campaign/${campaign.id}/report`)}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { color: PRIMARY_MAIN, bgcolor: `${PRIMARY_MAIN}10` }
                              }}
                            >
                              <DescriptionIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleEditCampaign(campaign)}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { color: PRIMARY_MAIN, bgcolor: `${PRIMARY_MAIN}10` }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deletar">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setConfirmModalOpen(true);
                                setDeletingCampaign(campaign);
                              }}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { color: "error.main", bgcolor: "rgba(239, 68, 68, 0.08)" }
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}

                {loading && <TableRowSkeleton columns={8} />}
              </TableBody>
            </Table>
          </TableContainer>

          {loading && campaigns.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Paper>
      </Container>

      {/* Modais */}
      <ConfirmationModal
        title={
          deletingCampaign
            ? `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${deletingCampaign.name}?`
            : ""
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      {campaignModalOpen && (
        <CampaignModal
          resetPagination={() => {
            setPageNumber(1);
            fetchCampaigns();
          }}
          open={campaignModalOpen}
          onClose={handleCloseCampaignModal}
          aria-labelledby="form-dialog-title"
          campaignId={selectedCampaign && selectedCampaign.id}
        />
      )}
    </Box>
  );
};

export default Campaigns;